// Pure helpers for the BO coin screens (asrth/mdf-bo-fn#294, coin D3-3 / D3-4;
// docs/specs/2026-09-30-coin-d3-ui-design.md §3 and §4 "bo-fn").
//
// Consumers:
//   src/views/Member/MemberDetail.vue      — coin balance field, coin log table,
//                                            "ปรับ coin" dialog
//   src/views/Member/MemberCoinsReport.vue — /members/coins-report
//
// Behaviour is specified in tests/unit/memberCoins.spec.js.
//
// TWO RULES THIS FILE EXISTS TO KEEP
//
//   1. The FE never decides a coin number. It validates the SHAPE of what an
//      operator typed (so the obvious mistakes are caught before a round trip)
//      and it FORMATS what the API returned. It never clamps an adjustment to
//      fit a balance and it never recomputes a balance from the log — the API
//      owns both (§3: "ห้าม clamp เงียบ — BO ต้องรู้").
//   2. Every string that reaches a human is an i18n KEY, because the legal
//      wording for "coin" (OP-4) has not landed yet and must be changeable in
//      th.js / en.js without touching a component.

// ---------------------------------------------------------------------------
// Adjustment validation
// ---------------------------------------------------------------------------

// Error keys returned by validateAdjust. They are i18n key SUFFIXES under
// `member_coins.err.*`, not sentences, so the dialog renders
// $t('member_coins.err.' + key) and the wording stays in the language files.
export const ERR_COINS_ZERO = 'coins_zero';
export const ERR_COINS_NAN = 'coins_nan';
export const ERR_COINS_RANGE = 'coins_range';
export const ERR_NOTE_TOO_SHORT = 'note_too_short';

// Mirrors the API's own limits (§3: 400 `bad_adjust` on 0, on a missing note,
// and on |coins| > 100000). Duplicated on purpose so the operator gets an
// instant, localised message — but the API remains the authority: the dialog
// still surfaces a 400 it did not predict.
export const MAX_ADJUST_COINS = 100000;
export const MIN_NOTE_LENGTH = 3;

// validateAdjust answers "would the API accept this form?" — returns the first
// error key, or null when the form is submittable.
//
// coins: a whole number, positive (give) or negative (take away). Accepts a
//   numeric string because an input bound with v-model (rather than .number)
//   hands over text, and el-input-number yields null when its box is cleared.
//   A fraction is ERR_COINS_NAN, not a rounded-off number: coins are counted,
//   and silently turning 1.5 into 1 or 2 would be the FE deciding an amount.
// note: mandatory, >= MIN_NOTE_LENGTH characters after trimming — it becomes
//   the audit trail (`"<note> · by <user>"`), so "-" is not an explanation.
//
// Order matters: the amount is reported before the note, because that is the
// field the operator is looking at when they get it wrong.
export function validateAdjust(form) {
  const f = form || {};

  const coins = normalizeAdjustCoins(f.coins);
  if (coins === null) return ERR_COINS_NAN;
  if (coins === 0) return ERR_COINS_ZERO;
  if (Math.abs(coins) > MAX_ADJUST_COINS) return ERR_COINS_RANGE;

  const note = typeof f.note === 'string' ? f.note.trim() : '';
  if (note.length < MIN_NOTE_LENGTH) return ERR_NOTE_TOO_SHORT;

  return null;
}

// normalizeAdjustCoins turns what the form holds into the integer the API will
// receive, or null when it is not an integer at all. Exported because the
// dialog sends this value rather than the raw model — one conversion, so the
// number that was validated is the number that is posted.
//
// null / undefined / '' (a cleared box) are "no amount yet" and come back as
// null, which validateAdjust reports as ERR_COINS_NAN — the submit button is
// disabled either way, and the message says "enter an amount" rather than
// accusing the operator of typing zero.
export function normalizeAdjustCoins(v) {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  if (typeof v !== 'number' && typeof v !== 'string') return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  if (!Number.isInteger(n)) return null;
  return n;
}

// adjustApiErrorKey maps the rejection the API sent to an i18n key suffix under
// `member_coins.err.*`, or null when the error is not one of the two documented
// codes (the caller then falls back to apiErrorMessage, which handles 5xx,
// network failures and unknown messages).
//
// The code arrives in `Msg`, not in a `code` field and not in the HTTP body
// text — that is the envelope this API has used since v2, and reading
// `data.message` (lowercase) is the mistake src/util/api-error.js was written
// to stop repeating.
export function adjustApiErrorKey(err) {
  const res = err && err.response;
  if (!res) return null;
  const msg = ((res.data && res.data.Msg) || '') + '';
  const code = msg.trim();
  if (res.status === 400 && code === 'bad_adjust') return 'bad_adjust';
  if (res.status === 409 && code === 'insufficient') return 'insufficient';
  return null;
}

// ---------------------------------------------------------------------------
// Coin log rows
// ---------------------------------------------------------------------------

// The six ledger types, verbatim from services/coinledger/coinledger.go.
// Anything else is rendered through `type_unknown` rather than shown raw: a
// type this build has never heard of is a deploy-order problem, not something
// an operator can act on.
export const COIN_TYPES = ['earn', 'burn', 'refund', 'clawback', 'expire', 'adjust'];

// Postgres/Go zero timestamps that mean "no expiry" rather than "expired in
// year 1". Both spellings reach the FE depending on the driver and the column
// type, and both used to render as a date in the table.
const ZERO_DATES = ['0001-01-01', '1970-01-01'];

// coinRowMeta packages one member_coin_log row for the template:
//
//   sign        '+' | '-' | ''    the character shown before the amount
//   colorClass  a Bootstrap text utility — never a hex, so the contrast gate
//               (tests/unit/contrast.spec.js) keeps covering these colours
//   typeLabelKey  full i18n key for the type badge
//   expiresText   'YYYY-MM-DD', or '' when the row cannot expire
//
// The amount itself is NOT formatted here: the table prints the absolute value
// with the caller's own thousands separator, and returning a pre-formatted
// string would mean two places decide what a number looks like.
export function coinRowMeta(row) {
  const r = row || {};
  const coins = Number(r.coins);
  const n = Number.isFinite(coins) ? coins : 0;

  let sign = '';
  let colorClass = 'text-muted';
  if (n > 0) {
    sign = '+';
    colorClass = 'text-success';
  } else if (n < 0) {
    sign = '-';
    colorClass = 'text-danger';
  }

  const type = typeof r.type === 'string' ? r.type.trim() : '';
  const typeLabelKey = COIN_TYPES.indexOf(type) !== -1
    ? 'member_coins.type_' + type
    : 'member_coins.type_unknown';

  return { sign, colorClass, typeLabelKey, expiresText: coinExpiresText(r.expires_at) };
}

// coinExpiresText renders an expiry as the plain calendar day the API sent.
//
// It slices the ISO string instead of parsing it into a Date on purpose: the
// API sends wall-clock Bangkok time, and `new Date(...).toLocaleDateString()`
// in a browser set to another zone would shift the day the coins die. Shipped
// bugs in this repo follow that exact shape (see fix-wms-date-literal-z).
export function coinExpiresText(v) {
  if (!v) return '';
  const day = String(v).trim().substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  if (ZERO_DATES.indexOf(day) !== -1) return '';
  return day;
}

// ---------------------------------------------------------------------------
// Liability report
// ---------------------------------------------------------------------------

// The eight cards of the liability report, in the order they are rendered.
//
// They are not eight interesting numbers picked by taste — they are the
// RECONCILIATION: opened + earn + burn + refund + clawback + expire + adjust
// must equal closing. Laid out in that order, an operator can read the month
// down the row and see where the balance moved; a set that did not add up
// would be visible on the page instead of hiding in a spreadsheet.
//
// The two remaining figures from §3 (members_with_balance, expiring_next_month)
// are counts of MEMBERS, not coin movements — mixing them into this row would
// break the arithmetic above, so the view shows them on their own line.
export const LIABILITY_CARD_FIELDS = [
  'opened_balance',
  'earn',
  'burn',
  'refund',
  'clawback',
  'expire',
  'adjust',
  'closing_balance',
];

// liabilityCards turns the API payload into [{ key, value }] where `key` is the
// full i18n key of the card label and `value` is an integer.
//
// Signs are passed through UNCHANGED. The API reports outflows as negative
// (§5 #5: "burn −100", "expire −50"), so negating here would double the sign
// and break the reconciliation the order above promises. A missing or
// unreadable figure becomes 0 — the card must render, and a blank card in a
// liability report reads as "nothing happened" to everyone who sees it.
export function liabilityCards(data) {
  const d = data || {};
  return LIABILITY_CARD_FIELDS.map((field) => ({
    key: 'member_coins.liability.' + field,
    value: toInt(d[field]),
  }));
}

// toInt is deliberately strict about what counts as a number and permissive
// about the container: a bigint-ish total serialised as a string still has to
// show up on the card, while an object or an array must not become 0-by-
// coercion and pretend to be data (Number([]) is 0).
function toInt(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : 0;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '') return 0;
    const n = Number(t);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Month picker
// ---------------------------------------------------------------------------

// monthOptions builds the el-select list for the report, newest first:
// `now`'s own month, then the n-1 months before it.
//
//   monthOptions(new Date('2026-09-30'), 3)
//     -> [{value:'2026-09',...}, {value:'2026-08',...}, {value:'2026-07',...}]
//
// `label` equals `value` — 'YYYY-MM' is exactly the string the API's ?month=
// takes and exactly what an operator quotes when they report a figure, and
// this helper has no locale to render "กันยายน 2569" with. A view that wants a
// prettier label can map over the result; the VALUE must stay machine-shaped.
//
// Months step through the year with a day-1 anchor, never by subtracting 30
// days: on the 31st, `setMonth(m - 1)` on today's date rolls forward into the
// month it was supposed to step back from, and the list would skip one.
//
// An unusable `now` or a non-positive `n` yields [] rather than quietly
// substituting the real clock — a caller passing garbage should render an empty
// picker they can see, not a list that looks right for the wrong month.
export function monthOptions(now, n) {
  const count = normalizeCount(n === undefined ? 12 : n);
  if (count === 0) return [];

  const base = toDate(now);
  if (!base) return [];

  const out = [];
  const year = base.getFullYear();
  const month = base.getMonth(); // 0-11
  for (let i = 0; i < count; i++) {
    const d = new Date(year, month - i, 1);
    const value = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    out.push({ value, label: value });
  }
  return out;
}

// Local calendar, not UTC: the operator picking "this month" means the month on
// the wall behind them. The API resolves ?month= in Bangkok time, which is the
// zone every BO browser is in.
function toDate(v) {
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  return null;
}

// At most ten years of history in a dropdown; past that it is a date range, not
// a picker.
const MAX_MONTHS = 120;

function normalizeCount(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return 0;
  const c = Math.trunc(n);
  if (c <= 0) return 0;
  return c > MAX_MONTHS ? MAX_MONTHS : c;
}

export default {
  validateAdjust,
  normalizeAdjustCoins,
  adjustApiErrorKey,
  coinRowMeta,
  coinExpiresText,
  liabilityCards,
  monthOptions,
};
